import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  TrendingUp,
  EyeOff,
  Tag,
  Filter,
  Plus,
  Trash2,
  ChevronDown,
} from 'lucide-react';

const RULE_TYPES = [
  {
    key: 'boost_brand',
    label: 'Impulsar marca',
    icon: TrendingUp,
    color: 'indigo',
    description: 'Sube productos de una marca al top de los resultados',
    placeholder: 'Ej: Honda, Yamaha, Cagiva...',
    hasFactor: true,
  },
  {
    key: 'boost_product',
    label: 'Impulsar producto',
    icon: Tag,
    color: 'emerald',
    description: 'Sube un producto específico (por ID externo)',
    placeholder: 'Ej: 70722, SKU-123...',
    hasFactor: true,
  },
  {
    key: 'hide_product',
    label: 'Ocultar producto',
    icon: EyeOff,
    color: 'red',
    description: 'Excluye un producto de todos los resultados de búsqueda',
    placeholder: 'Ej: 70722, SKU-123...',
    hasFactor: false,
  },
  {
    key: 'filter_category',
    label: 'Priorizar categoría',
    icon: Filter,
    color: 'amber',
    description: 'Da mayor peso a productos de una categoría concreta',
    placeholder: 'Ej: Frenos, Neumáticos, Aceites...',
    hasFactor: true,
  },
];

const TYPE_META = Object.fromEntries(RULE_TYPES.map((t) => [t.key, t]));

const COLOR_MAP = {
  indigo: {
    bg: 'bg-indigo-500/10',
    border: 'border-indigo-500/30',
    text: 'text-indigo-400',
    badge: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  },
  emerald: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
    badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  },
  red: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    text: 'text-red-400',
    badge: 'bg-red-500/15 text-red-300 border-red-500/30',
  },
  amber: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
    badge: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  },
};

function RuleCard({ rule, onToggle, onDelete }) {
  const meta = TYPE_META[rule.rule_type] || {};
  const colors = COLOR_MAP[meta.color] || COLOR_MAP.indigo;
  const Icon = meta.icon || Tag;

  return (
    <div
      data-testid={`rule-card-${rule.rule_id}`}
      className={`flex items-center gap-4 rounded-xl border px-4 py-3 transition-all group ${
        rule.is_active
          ? `${colors.bg} ${colors.border}`
          : 'bg-gray-800/30 border-gray-700/50 opacity-60'
      }`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          rule.is_active ? colors.bg : 'bg-gray-700/40'
        }`}
      >
        <Icon size={15} className={rule.is_active ? colors.text : 'text-gray-500'} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full border ${colors.badge}`}
          >
            {meta.label || rule.rule_type}
          </span>
          <span className="text-white font-medium text-sm truncate">{rule.label || rule.value}</span>
          {rule.value !== rule.label && (
            <span className="text-gray-500 text-xs truncate">({rule.value})</span>
          )}
        </div>
        {meta.hasFactor && rule.boost_factor && (
          <p className="text-gray-500 text-xs mt-0.5">
            Factor: ×{rule.boost_factor}
          </p>
        )}
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <Switch
          data-testid={`rule-toggle-${rule.rule_id}`}
          checked={rule.is_active}
          onCheckedChange={(v) => onToggle(rule.rule_id, v)}
          aria-label="Activar/desactivar regla"
        />
        <button
          data-testid={`rule-delete-${rule.rule_id}`}
          onClick={() => onDelete(rule.rule_id)}
          className="text-gray-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
          title="Eliminar regla"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

export default function MerchandisingPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('boost_brand');
  const [formValue, setFormValue] = useState('');
  const [formFactor, setFormFactor] = useState('2');
  const [formLabel, setFormLabel] = useState('');

  const fetchRules = useCallback(async () => {
    try {
      const res = await api.get('/api/merchandising/rules');
      setRules(res.data.rules || []);
    } catch {
      setError('No se pudieron cargar las reglas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formValue.trim()) return;
    setSaving(true);
    setError('');
    try {
      const meta = TYPE_META[formType];
      await api.post('/api/merchandising/rules', {
        rule_type: formType,
        value: formValue.trim(),
        boost_factor: meta.hasFactor ? parseFloat(formFactor) || 2.0 : null,
        label: formLabel.trim() || formValue.trim(),
      });
      setFormValue('');
      setFormLabel('');
      setFormFactor('2');
      setShowForm(false);
      await fetchRules();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al crear la regla');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (ruleId, active) => {
    setRules((prev) =>
      prev.map((r) => (r.rule_id === ruleId ? { ...r, is_active: active } : r))
    );
    try {
      await api.put(`/api/merchandising/rules/${ruleId}`, { is_active: active });
    } catch {
      await fetchRules();
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('¿Eliminar esta regla?')) return;
    setRules((prev) => prev.filter((r) => r.rule_id !== ruleId));
    try {
      await api.delete(`/api/merchandising/rules/${ruleId}`);
    } catch {
      await fetchRules();
    }
  };

  const selectedMeta = TYPE_META[formType];
  const activeCount = rules.filter((r) => r.is_active).length;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white" data-testid="merchandising-title">
          Merchandising
        </h1>
        <p className="text-gray-400 text-sm mt-1">
          Controla cómo aparecen tus productos en los resultados de búsqueda sin tocar el código.
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {RULE_TYPES.map((rt) => {
          const count = rules.filter((r) => r.rule_type === rt.key && r.is_active).length;
          const colors = COLOR_MAP[rt.color];
          const Icon = rt.icon;
          return (
            <div
              key={rt.key}
              className="bg-gray-900 border border-gray-800 rounded-xl p-3 flex flex-col gap-1"
            >
              <Icon size={14} className={colors.text} />
              <span className="text-xl font-bold text-white">{count}</span>
              <span className="text-gray-500 text-xs leading-tight">{rt.label}</span>
            </div>
          );
        })}
      </div>

      {error && (
        <div
          data-testid="merchandising-error"
          className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-xl px-4 py-3"
        >
          {error}
        </div>
      )}

      {/* Rules list */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-white font-semibold text-sm">
            Reglas activas{' '}
            {activeCount > 0 && (
              <Badge className="ml-2 bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 text-xs">
                {activeCount}
              </Badge>
            )}
          </h2>
          <Button
            data-testid="add-rule-btn"
            size="sm"
            onClick={() => setShowForm((v) => !v)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 text-xs"
          >
            <Plus size={13} />
            Nueva regla
          </Button>
        </div>

        {/* New rule form */}
        {showForm && (
          <form
            data-testid="new-rule-form"
            onSubmit={handleCreate}
            className="bg-gray-900 border border-gray-700 rounded-2xl p-5 space-y-4"
          >
            <h3 className="text-white font-medium text-sm">Nueva regla de merchandising</h3>

            {/* Type selector */}
            <div className="grid grid-cols-2 gap-2">
              {RULE_TYPES.map((rt) => {
                const Icon = rt.icon;
                const colors = COLOR_MAP[rt.color];
                const active = formType === rt.key;
                return (
                  <button
                    key={rt.key}
                    type="button"
                    data-testid={`rule-type-${rt.key}`}
                    onClick={() => setFormType(rt.key)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-left transition-all text-sm ${
                      active
                        ? `${colors.bg} ${colors.border} ${colors.text} font-medium`
                        : 'border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300'
                    }`}
                  >
                    <Icon size={14} className="flex-shrink-0" />
                    {rt.label}
                  </button>
                );
              })}
            </div>

            {/* Description */}
            <p className="text-gray-500 text-xs">{selectedMeta?.description}</p>

            {/* Value */}
            <div className="space-y-1.5">
              <label className="text-gray-400 text-xs font-medium">
                {formType === 'boost_brand' ? 'Nombre de marca' :
                 formType === 'hide_product' || formType === 'boost_product' ? 'ID externo del producto' :
                 'Nombre de categoría'}
              </label>
              <input
                data-testid="rule-value-input"
                value={formValue}
                onChange={(e) => setFormValue(e.target.value)}
                placeholder={selectedMeta?.placeholder}
                required
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-indigo-500"
              />
            </div>

            {/* Label */}
            <div className="space-y-1.5">
              <label className="text-gray-400 text-xs font-medium">
                Etiqueta (opcional)
              </label>
              <input
                data-testid="rule-label-input"
                value={formLabel}
                onChange={(e) => setFormLabel(e.target.value)}
                placeholder="Nombre descriptivo para esta regla"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-indigo-500"
              />
            </div>

            {/* Factor (only for boost rules) */}
            {selectedMeta?.hasFactor && (
              <div className="space-y-1.5">
                <label className="text-gray-400 text-xs font-medium">
                  Factor de impulso (×)
                </label>
                <select
                  data-testid="rule-factor-select"
                  value={formFactor}
                  onChange={(e) => setFormFactor(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500"
                >
                  <option value="1.5">×1.5 — leve</option>
                  <option value="2">×2 — moderado</option>
                  <option value="3">×3 — fuerte</option>
                  <option value="5">×5 — muy fuerte</option>
                  <option value="10">×10 — forzar al top</option>
                </select>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button
                data-testid="save-rule-btn"
                type="submit"
                disabled={saving || !formValue.trim()}
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
              >
                {saving ? 'Guardando...' : 'Guardar regla'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-white text-xs"
              >
                Cancelar
              </Button>
            </div>
          </form>
        )}

        {/* Rules */}
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" />
          </div>
        ) : rules.length === 0 ? (
          <div
            data-testid="no-rules-empty"
            className="text-center py-12 text-gray-500 bg-gray-900/40 border border-gray-800 rounded-2xl"
          >
            <TrendingUp size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No hay reglas de merchandising todavía.</p>
            <p className="text-xs mt-1 text-gray-600">
              Crea una regla para controlar cómo aparecen tus productos.
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="rules-list">
            {rules.map((rule) => (
              <RuleCard
                key={rule.rule_id}
                rule={rule}
                onToggle={handleToggle}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-5 space-y-3">
        <h3 className="text-white text-sm font-medium">Cómo funcionan las reglas</h3>
        <div className="space-y-2">
          {RULE_TYPES.map((rt) => {
            const Icon = rt.icon;
            const colors = COLOR_MAP[rt.color];
            return (
              <div key={rt.key} className="flex items-start gap-2.5 text-xs text-gray-400">
                <Icon size={13} className={`${colors.text} mt-0.5 flex-shrink-0`} />
                <div>
                  <span className="text-gray-300 font-medium">{rt.label}:</span>{' '}
                  {rt.description}.
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-600">
          Las reglas se aplican en tiempo real a cada búsqueda, antes del reranking por IA.
          Los factores de impulso se multiplican al score de Atlas Search.
        </p>
      </div>
    </div>
  );
}
